// Deals API file
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_BASE_PATH } from '../environment';

const BASE_URL = `${API_BASE_PATH}/deal`;
const BASE_URL1 = `${API_BASE_PATH}`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken') || '';
  return {
    Authorization: token,
    Accept: 'application/json',
  };
};

const showAxiosError = (error: any, fallback: string) => {
  const detail = error?.response?.data?.detail || error?.message || fallback;
  toast.error(detail);
  console.error(detail);
};

export interface DealPayload {
  deal_priority?: string;
  status?: string;
  deal_value?: string;
  currency?: string;
  due_date?: string;
  expected_closing_date?: string;
  follow_up_date?: string;
  Description?: string;
  contact_id?: string;
  product_id?: string;
  deal_source_id?: string;
  leadowner_id?: string;
  company_name?:string;
}

export interface Deal {
  deal_id: string;
  deal_priority: string;
  deal_value: string;
  currency: string;
  due_date: string;
  expected_closing_date: string;
  follow_up_date: string;
  Description: string;
  status: string;
  created_at: string;
  updated_at: string;
  dealsource: {
    deal_source_id: string;
    deal_source_nema: string;
  };
  product: {
    product_id: string;
    product_name: string;
    product_price: number;
  };
  contact: {
    contact_id: string;
    company_name: string;
    company_mail: string;
    contact_person_name: string;
   branch:[ {
  branch_id?: string;
  branch_name?: string;
  branch_address?: string;
  branch_city?: string;
  branch_state?: string;
  branch_zipcode?: string;
  branch_country?: string;
  gst_number?: string;
  
  }
]
  }
leads: {
  lead_id: string;
  lead_description: string;
  users: {
    id: string;
    name: string;
  };
  no_logins: number;
  rate_per_login: number;
  tax: number;
  amount: number;
  total_amount: number;
  proposal_discount: number;
  proposal_id: string;
  quotaiton_id: string;
  revision_id: string;
  proposal_tax_percentage: number;
  proposal_discount_percentage: number;
    cgst_percentage: number;
  sgst_percentage: number;
  igst_percentage: number;
  cgst: number;
  sgst: number;
  igst: number;
};

  client: {
    client_id: string;
  };
}

// ✅ Get Deal by ID
export const fetchDealById = async (deal_id: string): Promise<Deal> => {
  try {
    const res = await axios.get(`${BASE_URL1}get_deal/${deal_id}`, {
      headers: getAuthHeaders(),
    });
    return res.data[0];
  } catch (error) {
    showAxiosError(error, 'Failed to fetch deal');
    throw error;
  }
};

// ✅ Get All Deals
export const fetchAllDeals = async (): Promise<Deal[]> => {
  try {
    const res = await axios.get(`${BASE_URL}/all/get`, {
      headers: getAuthHeaders(),
    });
    return res.data;
  } catch (error) {
    showAxiosError(error, 'Failed to fetch all deals');
    throw error;
  }
};

// ✅ Update Deal
export const updateDeal = async (deal_id: string, data: DealPayload): Promise<string> => {
  try {
    const form = new URLSearchParams();
    for (const key in data) {
      const value = data[key as keyof DealPayload];
      if (value !== null && value !== undefined) {
        form.append(key, value);
      }
    }

    const res = await axios.put(`${BASE_URL}/${deal_id}/update`, form, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        ...getAuthHeaders(),
      },
    });

    return res.data;
  } catch (error) {
    showAxiosError(error, 'Failed to update deal');
    throw error;
  }
};

// ✅ Delete Deal
export const deleteDeal = async (deal_id: string): Promise<void> => {
  try {
    await axios.delete(`${BASE_URL}/${deal_id}/delete`, {
      headers: getAuthHeaders(),
    });
    toast.success('Deal deleted successfully');
  } catch (error) {
    showAxiosError(error, 'Failed to delete deal');
    throw error;
  }
};

export const deleteDealsBulk = async (dealIds: string[]): Promise<void> => {
  try {
    if (!dealIds || dealIds.length === 0) return;

    // Convert array → "D0018,D0020"
    const idsParam = dealIds.join(",");

    await axios.delete(
      `${BASE_URL}-bulk-delete?deal_ids=${encodeURIComponent(idsParam)}`,
      {
        headers: {
          ...getAuthHeaders(),
          accept: "application/json",
        },
      }
    );

    toast.success("Deals deleted successfully");
  } catch (error) {
    showAxiosError(error, "Failed to bulk delete deals");
    throw error;
  }
};

export { getAuthHeaders, showAxiosError };

export const getProductRevenueData = (deals: Deal[]) => {
  const revenueMap: Record<string, number> = {};

  deals.forEach((deal) => {
    if (deal.status === "won") {
      const name = deal.product?.product_name || "Unknown";
      const value = parseFloat(deal.deal_value) || 0;
      revenueMap[name] = (revenueMap[name] || 0) + value;
    }
  });

  return Object.entries(revenueMap).map(([name, value]) => ({
    name,
    value
  }));
};

// ✅ Get Deals by User ID
export const fetchDealsByUserId = async (user_id: string): Promise<Deal[]> => {
  try {
    const res = await axios.get(`${BASE_URL}/get/${user_id}`, {
      headers: getAuthHeaders(),
    });
    return res.data; // Assuming the backend returns an array of deals
  } catch (error) {
    showAxiosError(error, 'Failed to fetch deals for user');
    throw error;
  }
};

// 🚀 Export Deals - PDF and Excel (Customized)
export const exportDeals = async (
  type: 'pdf' | 'excel',
  fields: string[],
  deal_ids: string[]
): Promise<string> => {
  try {
    const endpoint =
      type === 'pdf'
        ? `${BASE_URL1}/export/deals/pdf/customized`
        : `${BASE_URL1}/export/deals/excel/customized`;

    const params = new URLSearchParams();
    params.append('fields', fields.join(','));
    deal_ids.forEach((id) => params.append('deal_ids', id));

    const res = await axios.get(endpoint, {
      headers: getAuthHeaders(),
      params,
    });

    toast.success(`Deals ${type.toUpperCase()} export generated successfully`);
    return res.data; // this will be the URL or download path
  } catch (error) {
    showAxiosError(error, `Failed to export deals as ${type.toUpperCase()}`);
    throw error;
  }
};